import { useState, useEffect } from "react";
import { Save, Loader2, FileText, Home, MapPin, Star, Megaphone, Layout } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import axios from "axios";
import { toast } from "sonner";
import AdminLayout from "@/components/AdminLayout";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SECTIONS = [
  {
    id: "hero",
    title: "Hero Section",
    icon: Home,
    description: "The main banner area visitors see first",
    fields: [
      { key: "hero_title", label: "Hero Title", type: "input" },
      { key: "hero_subtitle", label: "Hero Subtitle", type: "input" },
    ]
  },
  {
    id: "trust",
    title: "Trust Badge",
    icon: Megaphone,
    description: "The savings badge shown below the hero",
    fields: [
      { key: "trust_badge", label: "Badge Text", type: "input" },
    ]
  },
  {
    id: "about",
    title: "About Section",
    icon: Layout,
    description: "The 'About' area on the homepage",
    fields: [
      { key: "about_heading", label: "Heading", type: "input" },
      { key: "about_paragraph_1", label: "Paragraph 1", type: "textarea" },
      { key: "about_paragraph_2", label: "Paragraph 2", type: "textarea" },
      { key: "about_button", label: "Button Text", type: "input" },
    ]
  },
  {
    id: "location",
    title: "Location Section",
    icon: MapPin,
    description: "Location details shown on the homepage",
    fields: [
      { key: "location_heading", label: "Heading", type: "input" },
      { key: "location_subtitle", label: "Subtitle", type: "input" },
      { key: "location_1_name", label: "Location 1 Name", type: "input" },
      { key: "location_1_desc", label: "Location 1 Description", type: "input" },
      { key: "location_2_name", label: "Location 2 Name", type: "input" },
      { key: "location_2_desc", label: "Location 2 Description", type: "input" },
      { key: "location_3_name", label: "Location 3 Name", type: "input" },
      { key: "location_3_desc", label: "Location 3 Description", type: "input" },
    ]
  },
  {
    id: "reviews",
    title: "Reviews Section",
    icon: Star,
    description: "The guest reviews area on the homepage",
    fields: [
      { key: "reviews_heading", label: "Heading", type: "input" },
      { key: "reviews_subtitle", label: "Subtitle", type: "input" },
    ]
  },
  {
    id: "cta",
    title: "Call to Action",
    icon: Megaphone,
    description: "The booking call-to-action section",
    fields: [
      { key: "cta_heading", label: "Heading", type: "input" },
      { key: "cta_subtitle", label: "Subtitle", type: "input" },
      { key: "cta_button", label: "Button Text", type: "input" },
    ]
  },
  {
    id: "footer",
    title: "Footer & Contact",
    icon: Layout,
    description: "Footer content and contact information",
    fields: [
      { key: "footer_company", label: "Company Name", type: "input" },
      { key: "footer_email", label: "Email Address", type: "input" },
      { key: "footer_instagram_text", label: "Instagram Link Text", type: "input" },
      { key: "footer_instagram_url", label: "Instagram URL", type: "input" },
      { key: "contact_email", label: "Contact Email (Header)", type: "input" },
    ]
  },
  {
    id: "terms",
    title: "Terms & Conditions",
    icon: FileText,
    description: "Full terms and conditions page content (HTML supported)",
    fields: [
      { key: "terms_content", label: "Terms & Conditions Content", type: "richtext" },
    ]
  },
  {
    id: "privacy",
    title: "Privacy Policy",
    icon: FileText,
    description: "Full privacy policy page content (HTML supported)",
    fields: [
      { key: "privacy_content", label: "Privacy Policy Content", type: "richtext" },
    ]
  },
];

const AdminContent = () => {
  const [content, setContent] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState({});

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const response = await axios.get(`${API}/content`);
      setContent(response.data);
    } catch (error) {
      toast.error("Failed to load site content");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (key, value) => {
    setContent(prev => ({ ...prev, [key]: value }));
    setDirty(prev => ({ ...prev, [key]: true }));
  };

  const handleSaveSection = async (section) => {
    const updates = {};
    section.fields.forEach(f => {
      if (dirty[f.key]) {
        updates[f.key] = content[f.key] || "";
      }
    });
    if (Object.keys(updates).length === 0) {
      toast.info("No changes to save in this section");
      return;
    }
    setSaving(true);
    try {
      await axios.patch(`${API}/content`, updates);
      toast.success(`${section.title} saved!`);
      const saved = { ...dirty };
      section.fields.forEach(f => delete saved[f.key]);
      setDirty(saved);
    } catch (error) {
      toast.error("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAll = async () => {
    const updates = {};
    Object.keys(dirty).forEach(key => {
      updates[key] = content[key] || "";
    });
    if (Object.keys(updates).length === 0) {
      toast.info("No changes to save");
      return;
    }
    setSaving(true);
    try {
      await axios.patch(`${API}/content`, updates);
      toast.success("All changes saved!");
      setDirty({});
    } catch (error) {
      toast.error("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const hasDirtyFields = (section) => {
    return section.fields.some(f => dirty[f.key]);
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-accent" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div data-testid="admin-content">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-heading font-bold text-primary">
              Site Content
            </h1>
            <p className="text-slate-600 mt-1">Edit all text that appears on your website</p>
          </div>
          {Object.keys(dirty).length > 0 && (
            <Button
              onClick={handleSaveAll}
              className="bg-accent hover:bg-accent/90"
              disabled={saving}
              data-testid="save-all-button"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save All Changes
            </Button>
          )}
        </div>

        <div className="space-y-6">
          {SECTIONS.map((section) => {
            const Icon = section.icon;
            return (
              <Card key={section.id} data-testid={`section-${section.id}`}>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <Icon className="w-5 h-5 text-accent" />
                        {section.title}
                      </CardTitle>
                      <CardDescription>{section.description}</CardDescription>
                    </div>
                    {hasDirtyFields(section) && (
                      <Button
                        size="sm"
                        onClick={() => handleSaveSection(section)}
                        className="bg-accent hover:bg-accent/90"
                        disabled={saving}
                        data-testid={`save-${section.id}`}
                      >
                        <Save className="w-3 h-3 mr-1" />
                        Save
                      </Button>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {section.fields.map((field) => (
                    <div key={field.key}>
                      <Label htmlFor={field.key} className="text-sm font-medium">
                        {field.label}
                      </Label>
                      {field.type === "input" && (
                        <Input
                          id={field.key}
                          value={content[field.key] || ""}
                          onChange={(e) => handleChange(field.key, e.target.value)}
                          data-testid={`input-${field.key}`}
                        />
                      )}
                      {field.type === "textarea" && (
                        <Textarea
                          id={field.key}
                          value={content[field.key] || ""}
                          onChange={(e) => handleChange(field.key, e.target.value)}
                          rows={3}
                          data-testid={`textarea-${field.key}`}
                        />
                      )}
                      {field.type === "richtext" && (
                        <Textarea
                          id={field.key}
                          value={content[field.key] || ""}
                          onChange={(e) => handleChange(field.key, e.target.value)}
                          rows={15}
                          className="font-mono text-sm"
                          placeholder="Enter content here. HTML tags like <h2>, <p>, <ul>, <li> are supported."
                          data-testid={`richtext-${field.key}`}
                        />
                      )}
                    </div>
                  ))}
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminContent;
